import {Injectable} from "@angular/core";
import {ParameterRule} from "@ispm-client/modules/query-builder/models/query-builder-config";
import {FactorBaseService} from "./factor-base-service";

@Injectable()
export class PatientFactorService extends FactorBaseService{
  static readonly AGEVALUE = "agevalue";
  static readonly AGEUNITS = "ageunits";
  static readonly SEXASSIGNEDATBIRTH = "sexassignedatbirth";
  static readonly CLLSLLFITNESS = "cllsllfitness";
  static readonly LIFEEXPECTANCYVALUE = "lifeexpectancyvalue";
  static readonly LIFEEXPECTANCYUNITS = "lifeexpectancyunits";
  static readonly ATDIAGNOSIS = "atdiagnosis";
  static readonly CURRENTSTATUS = "currentstatus";
  static readonly EXPECTEDPOSTTREATMENT = "expectedposttreatment";
  static readonly GENERICPERFORMANCESTATUS = "genericperformancestatus";
  static readonly ECOG = "ecog";
  static readonly KARNOFSKY = "karnofsky";


  public getDisplayName(parameterRule: ParameterRule) : string {
    //create a dictionary of Rules where the attribute type is the key (e.g., gene, origin)
    this.parameterRulesMap = {};
    let parameterRules = parameterRule.rules;
    for (let rule of parameterRules) {
      this.parameterRulesMap[rule.field.toLowerCase()] = rule;
    }

    //get patient type
    let parameter: string = parameterRule.parameter.toLowerCase();
    let patientType: string = "";
    switch (parameter.toLowerCase()) {
      case "patientfitness": {
        patientType = this.patientFitness();
        break;
      }
      case "patientdemographics": {
        patientType = this.patientDemographics();
        break;
      }
      case "patientlifeexpectancy": {
        patientType = this.patientLifeExpectancy();
        break;
      }
      case "patientmenopause": {
        patientType = this.patientMenopause();
        break;
      }
      case "patientperformancestatus": {
        patientType = this.patientPerformanceStatus();
        break;
      }
    }
    return patientType;
  }

  private patientDemographics(): string {
    /*
    Patient – Demographics
    {Age – Value}{Age – Units}
    {Sex assigned at birth}
    */
    var results: string[] = new Array();

    this.ageValue(results);
    this.sexAssignedAtBirth(results);

    let delimitedString = results.join(PatientFactorService.SPACE);
    return delimitedString;
  }

  private patientMenopause(): string {
    /*
    Patient – Menopause
    {Current Status}
    ‘At diagnosis:’ {At Diagnosis}
    ‘Expected post Tx:’ {Expected Status Post Tx}
     */
    var results: string[] = new Array();
    let rule = null;

    //use template that has all possibilities
    this.currentStatus(results);
    this.atDiagnosis(results);
    this.expectedPostTreatment(results);

    let delimitedString = results.join(PatientFactorService.SPACE);
    return delimitedString;
  }

  private patientFitness(): string {
    /*
    Patient – Fitness
    {Fitness}
     */
    var results: string[] = new Array();
    let rule = null;

    this.fitness(results);

    let delimitedString = results.join(PatientFactorService.SPACE);
    return delimitedString;
  }

  private patientLifeExpectancy(): string {
    /*
    Patient – Life Expectancy
    {Life Expectancy – Value}{Life Expectancy – Units}
     */
    var results: string[] = new Array();
    let rule = null;

    this.lifeExpectancyValue(results);

    let delimitedString = results.join(PatientFactorService.SPACE);
    return delimitedString;
  }

  private patientPerformanceStatus(): string {
    /*
    Patient – Performance Status
    {Generic Performance Status}
    ‘ECOG’ {ECOG/WHO}
    KPS {Karnofsky}
     */
    var results: string[] = new Array();

    this.genericPerformanceStatus(results);
    this.ecog(results);
    this.karnofsky(results);
    let delimitedString = results.join(PatientFactorService.SPACE);
    return delimitedString;
  }

  private lifeExpectancyValue(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.LIFEEXPECTANCYVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      this.lifeExpectancyUnits(displayValue, results);
    }
  }

  private lifeExpectancyUnits(value: string, results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.LIFEEXPECTANCYUNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (parseInt(value) === 1) {
        displayValue = displayValue.substring(0, displayValue.length-1);
      }
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private expectedPostTreatment(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.EXPECTEDPOSTTREATMENT];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, "Expected post Tx: ");
    }
  }

  private sexAssignedAtBirth(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.SEXASSIGNEDATBIRTH];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private ageUnits(value: string, results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.AGEUNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (parseInt(value) === 1) {
        displayValue = displayValue.substring(0, displayValue.length-1);
      }
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private atDiagnosis(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.ATDIAGNOSIS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, "At diagnosis: ");
    }
  }

  private ageValue(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.AGEVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      this.ageUnits(displayValue, results);
    }
  }

  private currentStatus(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.CURRENTSTATUS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private karnofsky(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.KARNOFSKY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, "KPS: ");
    }
  }

  private genericPerformanceStatus(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.GENERICPERFORMANCESTATUS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private ecog(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.ECOG];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, "ECOG: ");
    }
  }

  private fitness(results: string[]) {
    let rule = this.parameterRulesMap[PatientFactorService.CLLSLLFITNESS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

}
